/**
 * Droits sur un CAS DE SIMULATION (`targets`) — RÈGLE UNIQUE, lue par le serveur
 * (`src/api/account.ts`) ET par l'écran (onglet « Cas de simulation » d'`AccountPage`).
 *
 * Décision de Valérie (Q1, 01/10/2026) :
 *  · ORG / ADM / COO : inchangés — créent, modifient et suppriment les cas de leur compte ;
 *  · MNG : crée ; ne MODIFIE que les cas dont il est le PROPRIÉTAIRE ; ne SUPPRIME AUCUN cas,
 *    même le sien ;
 *  · DÉSACTIVER / RÉACTIVER un cas (`archive`, construit le 01/10/2026 — `targets.archived_at`) :
 *    même règle que la modification — ORG / ADM / COO tous les cas du compte, le MNG les siens.
 *    Un cas désactivé n'est plus proposé pour s'entraîner ni pour composer ; rien n'est supprimé.
 *
 * « Modifier » couvre tout ce qui change le cas : ses champs, et ses personae (création,
 * modification, suppression, rattachement, détachement) — le tunnel crée le cas PUIS y rattache
 * ses personae, d'où un seul geste.
 *
 * Propriétaire : la personne qui a créé le cas (`createdBy`) ou, pour un cas personnel, celle à
 * qui il appartient (`ownerUserId`).
 *
 * Pur : aucune dépendance serveur, importable côté client.
 */

/** Profils qui conçoivent des cas (alignés sur les offres et les contextes). */
export const TARGET_PROFILES: readonly string[] = ["ADM", "COO", "MNG", "ORG"];

export type TargetAction = "create" | "edit" | "archive" | "delete";

type TargetUser = { id: string; profileType: string | null | undefined };
type TargetRef = { createdBy?: string | null; ownerUserId?: string | null; kind?: string | null };

export function estProprietaireDuCas(userId: string, target: TargetRef): boolean {
  return !!userId && (target.createdBy === userId || target.ownerUserId === userId);
}

/**
 * `user` peut-il faire `action` sur `target` ? `target` est ignoré pour la création.
 * L'appartenance du cas au compte reste contrôlée par l'appelant (serveur).
 */
export function canTargetAction(user: TargetUser, action: TargetAction, target?: TargetRef | null): boolean {
  if (!user.profileType || !TARGET_PROFILES.includes(user.profileType)) return false;
  if (action === "create") return true;
  // Cas générique de la banque Kestio (`kind = 'socle'`, sans compte) : lu par tous les clients,
  // modifié par aucun — ni ORG ni MNG (le serveur, qui cherche le cas DANS le compte, répond 404).
  if (target?.kind === "socle") return false;
  if (user.profileType !== "MNG") return true;
  if (action === "delete") return false;
  // "edit" et "archive" : le MNG, sur ses propres cas seulement.
  return !!target && estProprietaireDuCas(user.id, target);
}

/**
 * Modifier (ou supprimer du compte) une PERSONA — décision D1 de Valérie du 01/10/2026.
 *
 * Une persona est PARTAGÉE entre cas (liaison M↔N `target_personae`, plus l'ancien lien direct
 * `personae.target_id`) : la modifier modifie TOUS les cas où elle sert. Règle : `user` doit pouvoir
 * modifier CHACUN de ces cas (`canTargetAction(…, "edit")`). ORG / ADM / COO : inchangés. MNG : la
 * persona ne doit servir qu'à SES cas — sinon refus « seul l'organisateur du compte peut la
 * modifier » (`persona_shared_with_other_cases`) ; aucun geste « Dupliquer » n'existe, le message
 * n'en promet pas. Une persona rattachée à aucun cas reste modifiable.
 */
export function canEditPersonaUsedBy(user: TargetUser, casesUsingPersona: readonly TargetRef[]): boolean {
  if (!user.profileType || !TARGET_PROFILES.includes(user.profileType)) return false;
  return casesUsingPersona.every((c) => canTargetAction(user, "edit", c));
}
